/**
 * ServiceIntakeFields - Dynamic intake fields per selected service
 *
 * Renders typed intake fields from service-configs for each selected service.
 * Supports text, boolean, select (with Custom/Other), button-group, number,
 * textarea, and multi-text types. All dropdowns/button-groups have an "Other"
 * option with a text input fallback.
 *
 * Updated: Phase 2 - Service Plans integration
 * - Dynamically loads service plans from Firestore when field.dynamicOptions = 'service_plans'
 * - Shows pricing info if field.showPricing = true
 */

import { useState } from 'react';
import { DealCategory, DEAL_CATEGORY_LABELS } from '@/types/theo-workflow.types';
import type { ServiceModuleData } from '@/types/theo-workflow.types';
import { useLegacyServiceConfigsMap } from '@/hooks/useTradePack';
import type { FieldDef } from '@/data/service-configs';
import { useServicePlansByType } from '@/hooks/useServicePlans';
import type { ServicePlan } from '@/types/schema';
import { PlanSelectorField } from './PlanSelectorField';

interface Props {
  selectedServices: DealCategory[];
  moduleData: Partial<Record<DealCategory, ServiceModuleData>>;
  onChange: (category: DealCategory, fieldKey: string, value: unknown) => void;
  disabled?: boolean;
}

export function ServiceIntakeFields({ selectedServices, moduleData, onChange, disabled }: Props) {
  const serviceConfigs = useLegacyServiceConfigsMap();

  if (selectedServices.length === 0) {
    return (
      <p className="text-xs text-white/30 text-center py-4">
        Select services above to see intake fields
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {selectedServices.map((category) => {
        const config = serviceConfigs[category];
        if (!config || config.intakeFieldDefs.length === 0) return null;

        const fields = moduleData[category]?.intakeFields as unknown as Record<string, unknown> ?? {};
        return (
          <div key={category} className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider">
                {DEAL_CATEGORY_LABELS[category]} Details
              </h4>
              {/* Pre-site required badge hidden — logic retained */}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {config.intakeFieldDefs.map((field) => {
                // Check if field has conditional visibility
                if (field.conditionalOn) {
                  const conditionField = fields[field.conditionalOn.field];
                  const conditionMet = conditionField === field.conditionalOn.value;
                  if (!conditionMet) return null; // Hide field if condition not met
                }

                return (
                  <IntakeField
                    key={field.key}
                    field={field}
                    category={category}
                    value={fields[field.key]}
                    onChange={(val) => onChange(category, field.key, val)}
                    disabled={disabled}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface IntakeFieldProps {
  field: FieldDef;
  category?: DealCategory;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

function IntakeField({ field, category, value, onChange, disabled }: IntakeFieldProps) {
  return (
    <div className={field.type === 'textarea' || field.type === 'button-group' ? 'md:col-span-2' : ''}>
      <label className="text-xs text-white/50 mb-1.5 block">
        {field.label}
        {field.required && <span className="text-red-400 ml-0.5">*</span>}
      </label>

      {field.type === 'boolean' ? (
        <BooleanField value={value} onChange={onChange} disabled={disabled} />
      ) : field.type === 'select' && field.dynamicOptions === 'service_plans' ? (
        <DynamicPlanSelect field={field} category={category} value={value} onChange={onChange} disabled={disabled} />
      ) : field.type === 'select' ? (
        <SelectFieldWithCustom field={field} value={value} onChange={onChange} disabled={disabled} />
      ) : field.type === 'button-group' ? (
        <ButtonGroupField field={field} value={value} onChange={onChange} disabled={disabled} />
      ) : field.type === 'textarea' ? (
        <textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={2}
          disabled={disabled}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-secondary/50 resize-none disabled:opacity-50"
        />
      ) : field.type === 'number' ? (
        <input
          type="number"
          value={(value as number) ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          placeholder={field.placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
        />
      ) : field.type === 'multi-text' ? (
        <MultiTextInput
          values={(value as string[]) ?? []}
          onChange={onChange}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      ) : (
        <input
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
        />
      )}
    </div>
  );
}

// ============================================================================
// Boolean Toggle (Yes / No buttons)
// ============================================================================

function BooleanField({ value, onChange, disabled }: { value: unknown; onChange: (v: unknown) => void; disabled?: boolean }) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        disabled={disabled}
        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
          value === true
            ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border border-emerald-400/30 ring-1 ring-emerald-400/20'
            : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        disabled={disabled}
        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
          value === false
            ? 'bg-red-500/15 text-red-600 dark:text-red-300 border border-red-400/30 ring-1 ring-red-400/20'
            : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        No
      </button>
    </div>
  );
}

// ============================================================================
// Select with Custom/Other option
// ============================================================================

function SelectFieldWithCustom({ field, value, onChange, disabled }: IntakeFieldProps) {
  const strVal = (value as string) ?? '';
  const isCustom = strVal !== '' && !(field.options ?? []).includes(strVal);
  const [showCustom, setShowCustom] = useState(isCustom);

  return (
    <div className="space-y-2">
      <select
        value={showCustom ? '__custom__' : strVal}
        onChange={(e) => {
          if (e.target.value === '__custom__') {
            setShowCustom(true);
            onChange('');
          } else {
            setShowCustom(false);
            onChange(e.target.value);
          }
        }}
        disabled={disabled}
        className="w-full px-3 py-2 bg-brand-dark border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50 [&>option]:bg-brand-dark [&>option]:text-white"
      >
        <option value="">Select...</option>
        {field.options?.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
        <option value="__custom__">Other (custom)</option>
      </select>
      {showCustom && (
        <input
          type="text"
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter custom value..."
          disabled={disabled}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
        />
      )}
    </div>
  );
}

// ============================================================================
// Button Group (tap-to-select buttons + optional "Other")
// ============================================================================

function ButtonGroupField({ field, value, onChange, disabled }: IntakeFieldProps) {
  const strVal = (value as string) ?? '';
  const isCustom = strVal !== '' && !(field.options ?? []).map(o => o.toLowerCase()).includes(strVal.toLowerCase());
  const [showCustom, setShowCustom] = useState(isCustom);

  const isSelected = (opt: string) => {
    if (showCustom) return false;
    return opt.toLowerCase() === strVal.toLowerCase();
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {field.options?.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => {
              setShowCustom(false);
              onChange(opt);
            }}
            disabled={disabled}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              isSelected(opt)
                ? 'bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/40 ring-1 ring-brand-secondary/20'
                : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20 hover:text-white/70'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {opt}
          </button>
        ))}
        {field.allowCustom !== false && (
          <button
            type="button"
            onClick={() => {
              setShowCustom(true);
              onChange('');
            }}
            disabled={disabled}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              showCustom
                ? 'bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/40 ring-1 ring-brand-secondary/20'
                : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20 hover:text-white/70'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Other
          </button>
        )}
      </div>
      {showCustom && (
        <input
          type="text"
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter custom value..."
          disabled={disabled}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
        />
      )}
    </div>
  );
}

// ============================================================================
// Multi-Text Input
// ============================================================================

interface MultiTextInputProps {
  values: string[];
  onChange: (values: unknown) => void;
  placeholder?: string;
  disabled?: boolean;
}

function MultiTextInput({ values, onChange, placeholder, disabled }: MultiTextInputProps) {
  const addValue = () => {
    onChange([...values, '']);
  };

  const updateValue = (index: number, val: string) => {
    const updated = [...values];
    updated[index] = val;
    onChange(updated);
  };

  const removeValue = (index: number) => {
    onChange(values.filter((_: string, i: number) => i !== index));
  };

  return (
    <div className="space-y-1.5">
      {values.map((val: string, i: number) => (
        <div key={i} className="flex gap-2">
          <input
            type="text"
            value={val}
            onChange={(e) => updateValue(i, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => removeValue(i)}
            disabled={disabled}
            className="px-2 text-white/30 hover:text-red-400 transition-colors disabled:opacity-50"
          >
            &times;
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addValue}
        disabled={disabled}
        className="text-xs text-brand-secondary/70 hover:text-brand-secondary transition-colors disabled:opacity-50"
      >
        + Add
      </button>
    </div>
  );
}

// ============================================================================
// Dynamic Plan Select (loads service plans from Firestore)
// ============================================================================

interface DynamicPlanSelectProps {
  field: FieldDef;
  category?: DealCategory;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

function DynamicPlanSelect({ field, category, value, onChange, disabled }: DynamicPlanSelectProps) {
  const strVal = (value as string) ?? '';

  // Map deal category to service plan type
  const serviceTypeMap: Record<DealCategory, ServicePlan['serviceType']> = {
    [DealCategory.NBN]: 'NBN',
    [DealCategory.STARLINK]: 'Starlink',
    [DealCategory.VOIP]: 'VoIP',
    [DealCategory.IT]: 'Other',
    [DealCategory.SMART_HOME]: 'Other',
    [DealCategory.SECURITY]: 'Other',
    [DealCategory.ACCESS_CONTROL]: 'Other',
    [DealCategory.AV]: 'Other',
    [DealCategory.WIFI_EXTENSION]: 'Other',
    [DealCategory.FEE_FOR_SERVICE]: 'Other',
  };

  const serviceType: ServicePlan['serviceType'] = category ? serviceTypeMap[category] : 'Other';
  const { plans, loading } = useServicePlansByType(serviceType);

  return (
    <PlanSelectorField
      plans={plans}
      value={strVal}
      onChange={(v) => onChange(v)}
      loading={loading}
      disabled={disabled}
      showPricing={field.showPricing}
      allowCustom={field.allowCustom !== false}
    />
  );
}
